import { useCallback, useRef, useState } from 'react';
import { hasMediaDevices, insecureMediaMessage } from '../../../utils/mediaSupport';

/**
 * Mic capture → 16 kHz mono 16-bit PCM, emitted as fixed 20 ms frames (320 samples = 640 bytes)
 * the backend VAD expects.
 *
 * Uses an AudioWorklet (inline, via Blob URL — no extra public file) to pull raw Float32 from the
 * mic at the AudioContext's native rate, then downsamples + frames on the main thread. Exposes a
 * live input level (0..1) for the waveform/orb, plus mute and stop.
 *
 *   const cap = useAudioCapture();
 *   await cap.start({ onFrame: (int16Bytes) => ws.send(int16Bytes), deviceId });
 *   cap.setMuted(true); cap.stop();
 */
const TARGET_RATE = 16000;
const FRAME_SAMPLES = 320; // 20 ms @ 16 kHz

export const captureGainLimit = ({ lowQuality, speechLike, blockRms, noiseFloor, peak }) => (
  lowQuality || (speechLike && blockRms < 0.015 && blockRms >= noiseFloor * 4 && peak >= noiseFloor * 6)
    ? 12 : 8
);

export const classifyCaptureBlock = (noiseFloor, rms, peak, calibrated = true) => {
  const speechLike = calibrated && rms >= Math.max(0.0025, noiseFloor * 2.2)
    && peak >= Math.max(0.007, noiseFloor * 3.5);
  // A steady voice is still a voice. Only quiet, non-speech blocks may update
  // the ambient estimate; otherwise speech can train its own silence threshold.
  const next = !speechLike && (rms < noiseFloor * 1.2 || rms < 0.002)
    ? noiseFloor + (rms - noiseFloor) * 0.025 : noiseFloor;
  return { speechLike, noiseFloor: Math.max(0.0005, Math.min(0.04, next)) };
};

const WORKLET_SRC = `
class CapProcessor extends AudioWorkletProcessor {
  constructor(options) {
    super();
    this.epoch = options.processorOptions.epoch;
    this.port.onmessage = ({ data }) => { this.epoch = data.epoch; };
  }
  process(inputs) {
    const ch = inputs[0] && inputs[0][0];
    const detected = inputs[1] && inputs[1][0];
    if (ch && ch.length) this.port.postMessage({ epoch: this.epoch, raw: ch.slice(0), detection: detected && detected.slice(0) });
    return true;
  }
}
registerProcessor('cap-processor', CapProcessor);
`;

export default function useAudioCapture() {
  const ctxRef = useRef(null);
  const streamRef = useRef(null);
  const nodeRef = useRef(null);
  const onFrameRef = useRef(null);
  const signalSinkRef = useRef(null);
  const mutedRef = useRef(false);
  const generationRef = useRef(0);
  const captureEpochRef = useRef(0);
  const startPromiseRef = useRef(null);
  const recordingGainRef = useRef(null);
  const recordingStreamRef = useRef(null);
  const accRef = useRef(new Float32Array(0)); // leftover after framing
  const resampleTailRef = useRef(new Float32Array(0));
  const resamplePhaseRef = useRef(0);
  const gainRef = useRef(1);                  // software AGC gain (browser AGC is off to avoid clipping)
  const lowQualityRef = useRef(false);        // Bluetooth/narrowband mic → condition the AGC harder
  const noiseRef = useRef(0.002);
  const detectionRef = useRef({ noise: 0.002, levels: [], samples: 0, done: false });
  const calibrationRef = useRef({ levels: [], samples: 0, done: false });
  const lastMeterAtRef = useRef(0);
  const metricsRef = useRef({ noiseFloor: 0.002, speechRms: 0, peak: 0, clipped: false,
    rawPeak: 0, rawRms: 0, rawClipped: false, processedPeak: 0, processedRms: 0, processedClipped: false,
    lastFrameAt: 0, settings: {} });
  const [level, setLevel] = useState(0);
  const [active, setActive] = useState(false);
  const [stream, setStream] = useState(null);   // local input; recorders use the separately gated stream
  const [recordingStream, setRecordingStream] = useState(null);
  const [inputReady, setInputReady] = useState(false);
  const inputReadyRef = useRef(false);
  const [error, setError] = useState('');       // e.g. insecure context (http LAN IP) or denied
  const [lowQualityAudio, setLowQualityAudio] = useState(false);  // exposed: BT/quiet mic detected

  // append + downsample (linear) + cut into 20ms frames → Int16 → onFrame
  const handleSamples = useCallback((inputBlock, inRate) => {
    const float32 = inputBlock.raw || inputBlock;
    const filtered = inputBlock.detection || float32;
    const detector = detectionRef.current;
    let filteredEnergy = 0;
    let filteredPeak = 0;
    for (const value of filtered) {
      filteredEnergy += value * value;
      filteredPeak = Math.max(filteredPeak, Math.abs(value));
    }
    const filteredRms = Math.sqrt(filteredEnergy / Math.max(1, filtered.length));
    if (!detector.done) {
      detector.levels.push(filteredRms);
      detector.samples += filtered.length;
      if (detector.samples >= inRate * 0.6) {
        detector.levels.sort((a, b) => a - b);
        detector.noise = Math.max(0.0005, Math.min(0.04, detector.levels[Math.floor(detector.levels.length * 0.2)]));
        detector.done = true;
        detector.levels = [];
      }
    }
    const detection = classifyCaptureBlock(detector.noise, filteredRms, filteredPeak, detector.done);
    detector.noise = detection.noiseFloor;
    if (!inputReadyRef.current) {
      inputReadyRef.current = true;
      setInputReady(true);
      setError('');
    }
    try {
      signalSinkRef.current?.({ speechLike: detection.speechLike,
        durationMs: filtered.length / inRate * 1000, at: Date.now() });
    } catch { /* A UI reminder must never interrupt capture. */ }
    if (mutedRef.current || inputBlock.epoch !== captureEpochRef.current || !onFrameRef.current) return;

    let rawInputPeak = 0;
    let rawInputEnergy = 0;
    for (let i = 0; i < float32.length; i += 1) {
      rawInputPeak = Math.max(rawInputPeak, Math.abs(float32[i]));
      rawInputEnergy += float32[i] * float32[i];
    }
    const rawInputRms = Math.sqrt(rawInputEnergy / Math.max(1, float32.length));

    // Preserve the fractional resampling position between worklet blocks. Rounding each
    // 128-sample block independently drops samples at 48 kHz and creates regular clicks.
    const ratio = inRate / TARGET_RATE;
    const tail = resampleTailRef.current;
    const input = new Float32Array(tail.length + float32.length);
    input.set(tail);
    input.set(float32, tail.length);
    let cursor = resamplePhaseRef.current;
    const output = [];
    if (ratio >= 1) {
      while (cursor + ratio <= input.length) {
        const end = cursor + ratio;
        let sum = 0;
        for (let index = Math.floor(cursor); index < end; index += 1) {
          sum += input[index] * (Math.min(index + 1, end) - Math.max(index, cursor));
        }
        output.push(sum / ratio);
        cursor = end;
      }
    } else {
      while (cursor + 1 < input.length) {
        const index = Math.floor(cursor);
        const fraction = cursor - index;
        output.push(input[index] * (1 - fraction) + input[index + 1] * fraction);
        cursor += ratio;
      }
    }
    const consumed = Math.floor(cursor);
    resampleTailRef.current = input.slice(consumed);
    resamplePhaseRef.current = cursor - consumed;
    const down = Float32Array.from(output);
    if (!down.length) return;

    // Software AGC + limiter (browser autoGainControl is OFF — it was clipping to full scale,
    // which distorts speech AND amplifies room noise so the server VAD never detects silence).
    // Bring the level toward a safe target RMS, but never let a peak approach full scale.
    let peak = 0;
    let energy = 0;
    for (let i = 0; i < down.length; i += 1) {
      const v = Math.abs(down[i]);
      if (v > peak) peak = v;
      energy += down[i] * down[i];
    }
    const blockRms = Math.sqrt(energy / Math.max(1, down.length));
    const calibration = calibrationRef.current;
    if (!calibration.done) {
      calibration.levels.push(blockRms);
      calibration.samples += down.length;
      if (calibration.samples >= TARGET_RATE * 0.6) {
        const levels = calibration.levels.sort((a, b) => a - b);
        noiseRef.current = Math.max(0.0005, Math.min(0.04, levels[Math.floor(levels.length * 0.2)]));
        calibration.done = true;
        calibration.levels = [];
      }
    }
    // Track the lower-energy blocks as this microphone's ambient floor. Speech
    // must rise above that floor before gain is applied; a fixed 0.01/0.02 gate
    // dropped quiet earbud speech entirely.
    const classification = classifyCaptureBlock(noiseRef.current, blockRms, peak, calibration.done);
    noiseRef.current = classification.noiseFloor;
    const { speechLike } = classification;
    const low = lowQualityRef.current;
    const TARGET_RMS = 0.09;
    const MAX_GAIN = captureGainLimit({ lowQuality: low, speechLike, blockRms,
      noiseFloor: noiseRef.current, peak });
    const CEILING = 0.85;                       // hard ceiling — keeps int16 peak well under 32767
    const ATTACK = low ? 0.16 : 0.10;
    let desired;
    if (!speechLike) {
      desired = 1;
    } else {
      desired = Math.min(MAX_GAIN, TARGET_RMS / blockRms);
      if (peak * desired > CEILING) desired = peak > 1e-4 ? CEILING / peak : desired;  // limiter
    }
    // smooth so the gain doesn't pump on every block
    gainRef.current += (desired - gainRef.current) * ATTACK;
    const smoothedGain = speechLike ? gainRef.current : Math.min(1.2, gainRef.current);
    const g = peak > 0 ? Math.min(smoothedGain, CEILING / peak) : smoothedGain;
    for (let i = 0; i < down.length; i += 1) down[i] *= g;

    metricsRef.current = {
      ...metricsRef.current,
      noiseFloor: noiseRef.current,
      speechRms: speechLike ? blockRms * g : 0,
      peak: peak * g,
      clipped: metricsRef.current.rawClipped || rawInputPeak >= 0.98,
      rawPeak: Math.max(metricsRef.current.rawPeak, rawInputPeak),
      rawRms: Math.max(metricsRef.current.rawRms, rawInputRms),
      rawClipped: metricsRef.current.rawClipped || rawInputPeak >= 0.98,
      processedPeak: Math.max(metricsRef.current.processedPeak, peak * g),
      processedRms: Math.max(metricsRef.current.processedRms, blockRms * g),
      processedClipped: metricsRef.current.processedClipped || peak * g >= 0.98,
      lastFrameAt: Date.now(),
    };

    if (Date.now() - lastMeterAtRef.current >= 80) {
      lastMeterAtRef.current = Date.now();
      setLevel(Math.min(1, blockRms * g * 4));
    }

    // join with leftover, emit full 320-sample frames
    const prev = accRef.current;
    const merged = new Float32Array(prev.length + down.length);
    merged.set(prev, 0);
    merged.set(down, prev.length);

    let off = 0;
    while (merged.length - off >= FRAME_SAMPLES) {
      const pcm = new Int16Array(FRAME_SAMPLES);
      for (let i = 0; i < FRAME_SAMPLES; i += 1) {
        const s = Math.max(-1, Math.min(1, merged[off + i]));
        pcm[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
      }
      onFrameRef.current(pcm.buffer, {
        detectionSpeechLike: detection.speechLike, detectionNoiseFloor: detector.noise,
        rawSpeechLike: speechLike, rawRms: blockRms, rawPeak: peak,
        noiseFloor: noiseRef.current,
      });
      off += FRAME_SAMPLES;
    }
    accRef.current = merged.slice(off);
  }, []);

  const start = useCallback(({ onFrame, deviceId } = {}) => {
    onFrameRef.current = onFrame;
    if (startPromiseRef.current) return startPromiseRef.current;
    if (streamRef.current?.getAudioTracks()[0]?.readyState === 'live') {
      const generation = generationRef.current;
      return Promise.resolve(ctxRef.current?.state === 'suspended' ? ctxRef.current.resume() : undefined)
        .then(() => {
          if (generation !== generationRef.current) throw new DOMException('Microphone start cancelled', 'AbortError');
          return streamRef.current;
        });
    }
    const generation = ++generationRef.current;
    const operation = (async () => {
      let micStream;
      let ownedContext;
      let ownedRecording;
      try {
        if (!hasMediaDevices()) {
          const msg = insecureMediaMessage();
          setError(msg);
          throw new Error(msg);
        }
        setError('');
        inputReadyRef.current = false;
        setInputReady(false);
        micStream = await navigator.mediaDevices.getUserMedia({
          audio: {
            deviceId: deviceId ? { exact: deviceId } : undefined,
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: false, // Controlled gain below avoids browser amplification of noise.
            channelCount: 1,
            sampleRate: { ideal: 48000 },
          },
        });
        if (generation !== generationRef.current) throw new DOMException('Microphone start cancelled', 'AbortError');
        streamRef.current = micStream;

        // Detect narrowband Bluetooth input without blocking other microphone types.
        let lowQuality = false;
        let deviceHint = 'unknown';
        try {
          const track = micStream.getAudioTracks()[0];
          const settings = track?.getSettings?.() || {};
          const nativeRate = Number(settings.sampleRate) || 0;
          let label = track?.label || '';
          if (!label && settings.deviceId && navigator.mediaDevices.enumerateDevices) {
            const devices = await navigator.mediaDevices.enumerateDevices();
            label = devices.find((device) => device.deviceId === settings.deviceId)?.label || '';
          }
          const btLabel = /bluetooth|hands-?free|airpod|headset|wireless|hfp|hsp/i.test(label);
          if (/bluetooth|hands-?free|airpod|wireless|hfp|hsp/i.test(label)) deviceHint = 'bluetooth-like';
          lowQuality = btLabel || (nativeRate > 0 && nativeRate <= 16000);
        } catch { /* Device detection is best-effort. */ }
        if (generation !== generationRef.current) throw new DOMException('Microphone start cancelled', 'AbortError');
        lowQualityRef.current = lowQuality;
        setLowQualityAudio(lowQuality);
        try { metricsRef.current.settings = micStream.getAudioTracks()[0]?.getSettings?.() || {}; }
        catch { metricsRef.current.settings = {}; }
        metricsRef.current.deviceHint = deviceHint;
        noiseRef.current = 0.002;
        detectionRef.current = { noise: 0.002, levels: [], samples: 0, done: false };
        resampleTailRef.current = new Float32Array(0);
        resamplePhaseRef.current = 0;
        accRef.current = new Float32Array(0);
        calibrationRef.current = { levels: [], samples: 0, done: false };
        gainRef.current = 1;
        const { sampleRate, channelCount, echoCancellation, noiseSuppression, autoGainControl } = metricsRef.current.settings;
        console.info('[AI Interview mic] active capture settings', {
          sampleRate, channelCount, echoCancellation, noiseSuppression, autoGainControl, lowQuality, deviceHint,
        });

        const Ctx = window.AudioContext || window.webkitAudioContext;
        // Prefer a 16 kHz context; resample when the browser uses the device rate.
        let ctx;
        try { ctx = new Ctx({ sampleRate: TARGET_RATE }); } catch { ctx = new Ctx(); }
        ownedContext = ctx;
        ctxRef.current = ctx;
        if (ctx.state === 'suspended') await ctx.resume();

        const url = URL.createObjectURL(new Blob([WORKLET_SRC], { type: 'application/javascript' }));
        try { await ctx.audioWorklet.addModule(url); } finally { URL.revokeObjectURL(url); }
        if (generation !== generationRef.current) throw new DOMException('Microphone start cancelled', 'AbortError');

        const source = ctx.createMediaStreamSource(micStream);
        const node = new AudioWorkletNode(ctx, 'cap-processor', {
          numberOfInputs: 2, processorOptions: { epoch: captureEpochRef.current },
        });
        const detectionFilter = ctx.createBiquadFilter();
        detectionFilter.type = 'highpass';
        detectionFilter.frequency.value = 80;
        detectionFilter.Q.value = Math.SQRT1_2;
        node.port.onmessage = (e) => {
          if (generation === generationRef.current) handleSamples(e.data, ctx.sampleRate);
        };
        nodeRef.current = node;
        const recordingGain = ctx.createGain();
        recordingGain.gain.value = mutedRef.current ? 0 : 1;
        const recordingDestination = ctx.createMediaStreamDestination();
        ownedRecording = recordingDestination.stream;
        source.connect(recordingGain);
        recordingGain.connect(recordingDestination);

        // Pull the graph without echoing the mic to the speakers.
        const sink = ctx.createGain();
        sink.gain.value = 0;
        source.connect(node, 0, 0);
        source.connect(detectionFilter);
        detectionFilter.connect(node, 0, 1);
        node.connect(sink);
        sink.connect(ctx.destination);

        ctxRef.current = ctx;
        streamRef.current = micStream;
        recordingGainRef.current = recordingGain;
        recordingStreamRef.current = ownedRecording;
        setStream(micStream);
        setRecordingStream(ownedRecording);
        setActive(true);
        return micStream;
      } catch (err) {
        micStream?.getTracks().forEach((track) => track.stop());
        ownedRecording?.getTracks().forEach((track) => track.stop());
        if (ownedContext) await ownedContext.close().catch(() => {});
        if (generation === generationRef.current) {
          ctxRef.current = null;
          nodeRef.current = null;
          streamRef.current = null;
          recordingGainRef.current = null;
          recordingStreamRef.current = null;
          setStream(null);
          setRecordingStream(null);
          setActive(false);
          setError(err.message || 'Microphone unavailable');
        }
        throw err;
      }
    })();
    startPromiseRef.current = operation;
    const settled = () => { if (startPromiseRef.current === operation) startPromiseRef.current = null; };
    operation.then(settled, settled);
    return operation;
  }, [handleSamples]);

  const setMuted = useCallback((m) => {
    if (mutedRef.current !== m) {
      captureEpochRef.current += 1;
      nodeRef.current?.port.postMessage({ epoch: captureEpochRef.current });
    }
    mutedRef.current = m;
    if (recordingGainRef.current) recordingGainRef.current.gain.value = m ? 0 : 1;
    accRef.current = new Float32Array(0);
    resampleTailRef.current = new Float32Array(0);
    resamplePhaseRef.current = 0;
    if (m) setLevel(0);
  }, []);

  const stop = useCallback(() => {
    generationRef.current += 1;
    startPromiseRef.current = null;
    try { nodeRef.current && nodeRef.current.disconnect(); } catch { /* ignore */ }
    try { streamRef.current && streamRef.current.getTracks().forEach((t) => t.stop()); } catch { /* ignore */ }
    try { recordingStreamRef.current?.getTracks().forEach((t) => t.stop()); } catch { /* ignore */ }
    try { ctxRef.current && ctxRef.current.close(); } catch { /* ignore */ }
    nodeRef.current = null; streamRef.current = null; ctxRef.current = null;
    recordingGainRef.current = null; recordingStreamRef.current = null;
    inputReadyRef.current = false;
    accRef.current = new Float32Array(0);
    resampleTailRef.current = new Float32Array(0);
    resamplePhaseRef.current = 0;
    gainRef.current = 1;
    noiseRef.current = 0.002;
    calibrationRef.current = { levels: [], samples: 0, done: false };
    setActive(false);
    setLevel(0);
    setStream(null);
    setRecordingStream(null);
    setInputReady(false);
  }, []);

  const getMetrics = useCallback(() => metricsRef.current, []);
  const resetTurnMetrics = useCallback(() => {
    metricsRef.current = { ...metricsRef.current, rawPeak: 0, rawRms: 0, rawClipped: false,
      processedPeak: 0, processedRms: 0, processedClipped: false, clipped: false };
  }, []);
  const getStream = useCallback(() => streamRef.current, []);
  const setSignalSink = useCallback((sink) => { signalSinkRef.current = sink; }, []);
  return { start, stop, setMuted, setSignalSink, getMetrics, resetTurnMetrics, getStream,
    level, active, stream, recordingStream, inputReady, error, lowQualityAudio };
}
